import { 
    TextField, 
    CardActions, 
    Button
} from "@mui/material";
import moment from "moment";
import { useSelector } from "react-redux";
import React, { useEffect, useState } from 'react';

import { StyledLoginCard } from "../StyledComponents";
import SelectUserDropdown from "./SelectUserDropdown";
import { SnackBarProp } from "./SimpleSnackBar";
import { Announcement } from "../types/Announcement";
import { AppState } from "../reducers";
import { LoadableProfileData } from "../reducers/userProfiles";
import { postAnnouncement, updateAnnouncement } from "../api";
import { firebaseUser } from "../firebase";
import { useTranslation } from "react-i18next";
import SelectMultipleUserDropDown from "./SelectMultipleUserDropDown";
import { loggedIn } from "../actions/loggedIn";

interface NewAnnouncementTableProps {
    snackBar: SnackBarProp,
    setSnackbar: (props: SnackBarProp) => void,
    announcement?: Announcement,
    onClose: () => void,
    open: boolean
}

export default function NewAnnouncementTable(props: NewAnnouncementTableProps) {
    // const [newAnnouncement, setNewAnnouncement] = useState<Announcement>();
    // const [oldAnnouncement, setOldAnnouncement] = useState<Announcement>();
    const [announcement, setAnnouncement] = useState<Announcement>({
      code: "",
      title: "",
      receivers: {},
      message: "",
      createdAt: 0,
      createdBy: "",
      updatedAt: 0,
    });
    const [newReceivers, setNewReceivers] = useState<Array<string>>([]);
    const [titleError, setTitleError] = useState<boolean>(false);
    const [messageError, setMessageError] = useState<boolean>(false);
    const [isEmpty, setIsEmpty] = useState<boolean>(false);
    const loggedInUser = useSelector<AppState, firebaseUser | null>(state => state.loggedInUser);
    const userProfiles = useSelector<AppState, LoadableProfileData>(state => state.userProfiles);
    const {t} = useTranslation();

    useEffect(() => {
      if (props.announcement) {
        setAnnouncement({
          code: props.announcement.code,
          title: props.announcement.title,
          receivers: props.announcement.receivers,
          message: props.announcement.message,
          createdAt: props.announcement.createdAt,
          createdBy: props.announcement.createdBy,
          updatedAt: moment().valueOf(),
        });
      }
    }, [props])

    useEffect(() => {
      if (!props.announcement) {
        let temp = announcement;
        temp.receivers = resetReceivers();
        newReceivers.forEach(rec => { temp.receivers[rec] = true; });
        setAnnouncement(temp);
      }
    }, [newReceivers])

    const resetReceivers = () => {
      let tempReceivers: { [key: string]: boolean } = {};
      if (userProfiles.kind === 'loaded') {
        userProfiles.data.forEach(x => {
          tempReceivers = { ...tempReceivers, [x.user_id]: false };
        })
        tempReceivers = { ...tempReceivers, [loggedInUser?.uid?? ""]: true};
        const tempFiltered = Object.keys(tempReceivers).filter(x => x === "");
        tempFiltered.forEach(key => delete tempReceivers[key]);
      }
      return tempReceivers;
    }
  
    const onUserSelectChange = (selectedUserProfile: string[]) => {
      if (selectedUserProfile !== undefined) {
        setNewReceivers(selectedUserProfile);
      }
    }

    const onAnnouncementFieldChange = (name: keyof Announcement) => (
      e: React.ChangeEvent<HTMLInputElement>
    ) => {
      if (announcement) {
        setAnnouncement({ ...announcement, [name]: e.target.value })
      }
    }
  
    const submitAnnouncementButtonClicked = async () => {
      let isError: boolean = false;
      setTitleError(false);
      setMessageError(false);
      setIsEmpty(false);

      try {
        if (announcement) {
          if (!announcement.title || !announcement.message) { 
            setTitleError(!announcement.title);
            setMessageError(!announcement.message);
            isError = true;
          }
          if (!props.announcement && newReceivers.length === 0) {
            setIsEmpty(true);
            isError = true;
          }
          if (isError) return;

          if (props.announcement) {
            await updateAnnouncement(announcement);
            props.setSnackbar({ ...props.snackBar, open: true, message: t("newAnnouncementTable.message_success_update") });
            props.onClose();
            return;
          }

          await postAnnouncement({ ...announcement, createdBy: loggedInUser?.uid?? "", createdAt: moment().valueOf() });
          props.setSnackbar({ ...props.snackBar, open: true, message: t("newAnnouncementTable.message_success_send") });
          props.onClose();
          return;
        }
      } catch (e: any) {
        props.setSnackbar({ ...props.snackBar, open: true, message: e.response.message })
      }
    }
  
    return (
      <StyledLoginCard>
        {props.announcement === undefined &&
          <SelectMultipleUserDropDown emptySelect={isEmpty} multiple={true} onUserSelectChange={onUserSelectChange}/>
        }
        <TextField
          required
          label={t("newAnnouncementTable.label_title")}
          variant="outlined"
          onChange={onAnnouncementFieldChange("title")}
          fullWidth
          error={titleError}
          helperText={titleError? t("newAnnouncementTable.helper_text_title") : null}
          value={announcement.title}
          style={{ marginTop: 30 }}
        />
        <TextField
          required
          label={t("newAnnouncementTable.label_message")}
          variant="outlined"
          onChange={onAnnouncementFieldChange("message")}
          multiline
          value={announcement.message}
          rows={10}
          fullWidth
          error={messageError}
          helperText={messageError? t("newAnnouncementTable.helper_text_message") : null}
          style={{ marginTop: 30 }}
        />
        <CardActions>
          <Button onClick={submitAnnouncementButtonClicked} color="primary" variant="contained">{t("butt.submit")}</Button>
        </CardActions>        
      </StyledLoginCard>
    )
  }